import React, { Suspense } from 'react';
import { Switch } from 'react-router-dom';
import Page404 from 'components/error/page404';
import { urlMap } from 'routes/urlMap';
import FullPageLoader from 'components/common/loaders/FullPageLoader';
import WonoloSpinnerLoader from 'components/common/loaders/WonoloSpinnerLoader';
import {
  AuthRoute, lazyRetry, Route
} from './components';
import {
  EditJobRequestRoutes,
  EditTemplateRoutes,
  LegacyJobRequestRoutes,
  NewJobRequestRoutes,
  NewTemplateRoutes,
} from './jobFormRoutes';

const DashboardPage = lazyRetry(() => import(/* webpackChunkName: "DashboardPage" */ 'components/pages/dashboard'));
const CustomerSettingsEditor = lazyRetry(() => import(
  /* webpackChunkName: "CustomerSettingsEditor" */ 'components/pages/settings/company/customerSettingsEditor'
));
const TransactionDetails = lazyRetry(() => import(
  /* webpackChunkName: "TransactionDetails" */ 'components/pages/reports/transactions/transactionDetails'
));
const MagicAlgorithm = lazyRetry(() => import(
  /* webpackChunkName: "MagicAlgorithm" */ 'components/pages/magicAlgorithm'
));
const TryoutConfirmation = lazyRetry(() => import(
  /* webpackChunkName: "TryoutConfirmation" */ 'components/pages/tryoutConfirmation'
));
const JobsPage = lazyRetry(() => import(/* webpackChunkName: "JobsPage" */ 'components/pages/jobs'));
const ReportTransactionsPage = lazyRetry(() => import(
  /* webpackChunkName: "ReportTransactionsPage" */ 'components/pages/reports/charges'
));
const WorkforceListPage = lazyRetry(() => import(
  /* webpackChunkName: "WorkforceListPage" */ 'components/pages/workforce/workforcePage'
));
const WorkerSchedulePage = lazyRetry(() => import(
  /* webpackChunkName: "WorkerSchedulePage" */ 'components/pages/workerSchedule'
));
const WorkforceTeamsPage = lazyRetry(() => import(
  /* webpackChunkName: "WorkforceTeamsPage" */ 'components/pages/workforce/teams/index'
));
const WandasPage = lazyRetry(() => import(
  /* webpackChunkName: "WandasPage" */ 'components/pages/settings/wandas/index'
));
const VenuesPage = lazyRetry(() => import(/* webpackChunkName: "VenuesPage" */ 'components/pages/venues'));
const JobFormChooseTemplate = lazyRetry(() => import(
  /* webpackChunkName: "JobFormChooseTemplate" */ 'components/pages/jobRequestSelector/templates'
));
const VenuesCreateForm = lazyRetry(() => import(
  /* webpackChunkName: "VenuesCreateForm" */ 'components/pages/components/forms'
).then((module) => ({ default: module.CreateVenueForm })));
const VenuesEditForm = lazyRetry(() => import(
  /* webpackChunkName: "VenuesEditForm" */ 'components/pages/components/forms'
).then((module) => ({ default: module.EditVenueForm })));
const ProfilePage = lazyRetry(() => import(/* webpackChunkName: "ProfilePage" */ 'components/pages/quality'));
const PinsPage = lazyRetry(() => import(/* webpackChunkName: "PinsPage" */ 'components/pages/pins'));
const SummaryReport = lazyRetry(() => import(
  /* webpackChunkName: "SummaryReport" */ 'components/pages/reports/summaryReport'
));
const ImportPage = lazyRetry(() => import(
  /* webpackChunkName: "ImportPage" */ 'components/pages/import/filesTable'
));
const ImportDraftsPage = lazyRetry(() => import(
  /* webpackChunkName: "ImportDraftsPage" */ 'components/pages/import/draftsTable'
));
const JobRequestDetailsPage = lazyRetry(() => import(
  /* webpackChunkName: "JobRequestDetailsPage" */ 'components/pages/jobRequestDetails'
));
const MessagesPage = lazyRetry(() => import(/* webpackChunkName: "MessagesPage" */ 'components/pages/messages'));
const NotificationsPreferencesPage = lazyRetry(() => import(
  /* webpackChunkName: "NotificationsPreferencesPage" */ 'components/pages/settings/notifications'
));
const ManageShiftsTable = lazyRetry(() => import(
  /* webpackChunkName: "ManageShiftsTable" */ 'components/pages/manageShifts/manageShiftsTable'
));
const CreateManageShiftsForm = lazyRetry(() => import(
  /* webpackChunkName: "CreateManageShiftsForm" */ 'components/pages/manageShifts/manageShiftsForm'
).then((module) => ({ default: module.CreateManageShiftForm })));
const EditManageShiftsForm = lazyRetry(() => import(
  /* webpackChunkName: "EditManageShiftsForm" */ 'components/pages/manageShifts/manageShiftsForm'
).then((module) => ({ default: module.EditManageShiftForm })));
const CopyManageShiftsForm = lazyRetry(() => import(
  /* webpackChunkName: "EditManageShiftsForm" */ 'components/pages/manageShifts/manageShiftsForm'
).then((module) => ({ default: module.CopyManageShiftForm })));
const JobRequestDrafts = lazyRetry(() => import('components/pages/jobRequestDrafts/index'));
const CompanyPage = lazyRetry(() => import('components/pages/settings/company/myCompanyPage'));
const MyInfoPage = lazyRetry(() => import('components/pages/requestors/myInfo'));

const CreateRequestorForm = lazyRetry(() => import(
  /* webpackChunkName: "CreateRequestorForm" */ 'components/pages/requestors/requestorForm'
).then((module) => ({ default: module.CreateRequestorForm })));
const EditRequestorForm = lazyRetry(() => import(
  /* webpackChunkName: "EditRequestorForm" */ 'components/pages/requestors/requestorForm'
).then((module) => ({ default: module.EditRequestorForm })));

const JobsReport = lazyRetry(() => import('components/pages/reports/jobsReport'));
const JobsReportDetails = lazyRetry(() => import('components/pages/reports/jobsReport/jobsReportDetails').then(
  (module) => ({ default: module.JobsReportDetails })
));
const JobsReportTimeAndAttenance = lazyRetry(() => import('components/pages/reports/jobsReport/jobsReportDetails').then(
  (module) => ({ default: module.JobRequestDetailsTimeAndAttendance })
));
const JobsReportRoster = lazyRetry(() => import('components/pages/reports/jobsReport/jobsReportDetails').then(
  (module) => ({ default: module.JobRequestDetailsRoster })
));
const JobsReportNew = lazyRetry(() => import('components/pages/reports/jobsReport/jobsReportDetails').then(
  (module) => ({ default: module.JobRequestDetailsNew })
));
const InvoiceReport = lazyRetry(() => import('components/pages/reports/invoiceReport'));
const EnterpriseOverview = lazyRetry(() => import('components/pages/reports/enterpriseOverview'));
const ForgotPassword = lazyRetry(() => import('components/pages/forgotPassword'));
const SignIn = lazyRetry(() => import('components/pages/signIn'));
const ResetPassword = lazyRetry(() => import('components/pages/resetPassword'));

const TermsOfService = lazyRetry(() => import('components/pages/termsOfService'));

const ManagePositions = lazyRetry(() => import('components/pages/managePositions'));
const EditPosition = lazyRetry(() => import('components/pages/managePositions/edit'));
const CreatePosition = lazyRetry(() => import('components/pages/managePositions/create'));
const NewUserMoFloContainer = lazyRetry(() => import('components/pages/moFlo').then((module) => ({
  default: module.NewUserPage,
})));
const ReturningUserMoFloContainer = lazyRetry(() => import('components/pages/moFlo').then((module) => ({
  default: module.ReturningUserPage,
})));
const PostContainer = lazyRetry(() => import('components/pages/postContainer'));

const Locations = lazyRetry(() => import('components/pages/locations'));
const EditSchedule = lazyRetry(() => import('components/pages/schedules/edit'));
const CreateSchedule = lazyRetry(() => import('components/pages/schedules/create'));
const Schedules = lazyRetry(() => import('components/pages/schedules/list'));

const PageController = () => (
  <Suspense fallback={<WonoloSpinnerLoader />}>
    <Switch>
      <Route path={urlMap.SIGNIN} component={SignIn} />
      <Route path={urlMap.RESET_PASSWORD} component={ResetPassword} />
      <Route path="/index.html" component={FullPageLoader} />
      <Route path={urlMap.TERMS_OF_SERVICE} component={TermsOfService} />
      <Route exact path={urlMap.FORGOT} component={ForgotPassword} />
      <AuthRoute path={urlMap.MANAGE_JOBS} component={JobsPage} />
      <AuthRoute exact path={urlMap.HOME} component={JobsPage} />
      <AuthRoute extact path={urlMap.MAGIC} component={MagicAlgorithm} />
      <AuthRoute
        exact
        path={urlMap.EDIT_COMPANY_SETTINGS}
        component={CustomerSettingsEditor}
      />
      <AuthRoute
        exact
        path={urlMap.ACCOUNT_REPORTS}
        component={ReportTransactionsPage}
      />
      <AuthRoute
        exact
        path={urlMap.TRANSACTION_DETAILS}
        component={TransactionDetails}
      />
      <AuthRoute path={urlMap.NEW_JOB} component={NewJobRequestRoutes} />
      <AuthRoute
        exact
        path={urlMap.NEW_JOB_TEMPLATES}
        component={JobFormChooseTemplate}
      />
      <AuthRoute
        exact
        path={urlMap.JOB_REQUESTS_IMPORT}
        component={ImportPage}
      />
      <AuthRoute
        exact
        path={urlMap.JOB_REQUEST_IMPORTED_FILE}
        component={ImportDraftsPage}
      />
      <AuthRoute path={urlMap.EDIT_JOB} component={EditJobRequestRoutes} />
      <AuthRoute path={urlMap.NEW_TEMPLATE} component={NewTemplateRoutes} />
      <AuthRoute path={urlMap.EDIT_TEMPLATE} component={EditTemplateRoutes} />
      <AuthRoute
        exact
        path={urlMap.TRYOUT_CONFIRMATION}
        component={TryoutConfirmation}
      />
      <AuthRoute path={urlMap.WONOLOERS} component={WorkforceListPage} />
      <AuthRoute path={urlMap.TEAMS} component={WorkforceTeamsPage} />
      <AuthRoute
        exact
        path={urlMap.VENUES}
        component={Locations}
      />
      <AuthRoute
        exact
        path={urlMap.VENUE_CREATE}
        component={VenuesCreateForm}
      />
      <AuthRoute
        exact
        path={urlMap.POSITIONS}
        component={ManagePositions}
      />
      <AuthRoute
        exact
        path={urlMap.POSITIONS_CONFIGURATOR}
        component={EditPosition}
      />
      <AuthRoute
        exact
        path={urlMap.POSITIONS_CREATE}
        component={CreatePosition}
      />
      <AuthRoute exact path={urlMap.VENUE_EDIT} component={VenuesEditForm} />
      <AuthRoute exact path={urlMap.WANDAS} component={WandasPage} />
      {/* RAILS DEPENDENT ROUTE REMOVE ONCE UPDATED in edit.html.slim and new.html.slim */}
      <AuthRoute
        path={urlMap.NEW_JOB_LEGACY}
        component={LegacyJobRequestRoutes}
      />
      {/* ***** ***** */}
      <AuthRoute exact path={urlMap.ACTIVE_JOBS} component={Page404} />
      <AuthRoute exact path={urlMap.REPORT_USAGE} component={Page404} />
      <AuthRoute exact path={urlMap.REPORT_ATTENDANCE} component={JobsReport} />
      <AuthRoute exact path={urlMap.JOB_REPORT_NEW} component={JobsReportNew} />
      <AuthRoute
        exact
        path={urlMap.JOBS_REPORT_TIME_AND_ATTENDANCE}
        component={JobsReportTimeAndAttenance}
      />
      <AuthRoute
        exact
        path={urlMap.JOBS_REPORT_ROSTER}
        component={JobsReportRoster}
      />
      <AuthRoute
        exact
        path={urlMap.JOB_REPORT_MANAGE}
        component={JobsReportDetails}
      />
      <AuthRoute exact path={urlMap.FAQ} component={Page404} />
      <AuthRoute exact path={urlMap.CONTACT} component={Page404} />
      <AuthRoute exact path={urlMap.WORKERS} component={ProfilePage} />
      <AuthRoute
        exact
        path={urlMap.EDIT_REQUESTOR_INFO}
        component={EditRequestorForm}
      />
      <AuthRoute
        exact
        path={urlMap.ADD_REQUESTOR}
        component={CreateRequestorForm}
      />
      <AuthRoute exact path={urlMap.PINS} component={PinsPage} />
      <AuthRoute exact path={urlMap.INVOICE_REPORT} component={InvoiceReport} />
      <AuthRoute path={urlMap.SUMMARY_REPORT} component={SummaryReport} />
      <AuthRoute
        exact
        path={urlMap.JOB_REQUEST_DRAFTS_NEW}
        component={JobRequestDrafts}
      />
      <AuthRoute
        exact
        path={urlMap.JOB_REQUESTS}
        component={JobRequestDetailsPage}
      />
      <AuthRoute exact path={urlMap.MESSAGES} component={MessagesPage} />
      <AuthRoute exact path={urlMap.DASHBOARD} component={DashboardPage} />
      <AuthRoute
        exact
        path={urlMap.ENTERPRISE_OVERVIEW}
        component={EnterpriseOverview}
      />
      <AuthRoute
        exact
        path={urlMap.NOTIFICATION_PREFERENCES}
        component={NotificationsPreferencesPage}
      />
      <AuthRoute exact path={urlMap.MY_INFO} component={MyInfoPage} />
      <AuthRoute exact path={urlMap.COMPANY_PAGE} component={CompanyPage} />
      <AuthRoute
        exact
        path={urlMap.MANAGE_SHIFTS}
        component={ManageShiftsTable}
      />
      <AuthRoute
        exact
        path={urlMap.MANAGE_SHIFTS_CREATE}
        component={CreateManageShiftsForm}
      />
      <AuthRoute
        exact
        path={urlMap.MANAGE_SHIFTS_EDIT}
        component={EditManageShiftsForm}
      />
      <AuthRoute
        exact
        path={urlMap.MANAGE_SHIFTS_COPY}
        component={CopyManageShiftsForm}
      />
      <AuthRoute
        exact
        path={urlMap.CREATE_MODULAR_ORDER}
        component={PostContainer}
      />
      <AuthRoute
        exact
        path={urlMap.MODULAR_ORDER_FORM}
        component={NewUserMoFloContainer}
      />
      <AuthRoute
        exact
        path={urlMap.MODULAR_ORDER_FORM_RETURNING}
        component={ReturningUserMoFloContainer}
      />
      <AuthRoute
        exact
        path={urlMap.SCHEDULES}
        component={Schedules}
      />
      <AuthRoute
        exact
        path={urlMap.SCHEDULES_EDIT}
        component={EditSchedule}
      />
      {/* NOTE: 404 route must be at the bottom! */}
      <Route path={urlMap.NOT_FOUND} component={Page404} />
      <Route component={Page404} />
    </Switch>
  </Suspense>
);

export default PageController;
